import { type BrainstormChatMessage } from '@backend/routes/stream/brainstorm-chat'
import { CircleCheck, Info, Lightbulb, RefreshCw, Sparkles, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { MarkdownContent } from '@/components/ui/markdown'

interface Props {
  message: BrainstormChatMessage
  onTopicSelect?: (topic: string, refinedPrompt: string) => void
  onContentIdeaSelect?: (topic: string, contentIdea: string) => void
  onBrainstormMore?: (topic: string) => void
  isStreaming?: boolean
}

export function BrainstormMessageItem({
  message,
  onTopicSelect,
  onContentIdeaSelect,
  onBrainstormMore,
  isStreaming = false,
}: Props) {
  if (!message || !message.parts || !Array.isArray(message.parts)) {
    console.warn('Invalid message structure:', message)
    return null
  }

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-primary max-w-[85%] rounded-2xl px-4 py-3 text-white">
          {message.parts.map((part, i) => {
            if (part.type === 'text') {
              const text = typeof part.text === 'string' ? part.text : ''
              if (!text) return null
              return (
                <div key={`${message.id}-${part.type}-${i}`} className="text-sm">
                  {text}
                </div>
              )
            }
            return null
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%]">
        <TooltipProvider delayDuration={200}>
          {message.parts.map((part, i) => {
            if (part.type === 'text') {
              return (
                <div
                  key={`${message.id}-${part.type}-${i}`}
                  className="text-foreground mb-2 text-sm leading-relaxed">
                  <MarkdownContent isStreaming={isStreaming}>{part.text}</MarkdownContent>
                </div>
              )
            }

            if (part.type === 'tool-getTrendingTopics') {
              switch (part.state) {
                case 'input-streaming':
                case 'input-available':
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-border bg-card rounded-xl border p-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-lg">
                            <TrendingUp className="text-primary h-4 w-4 animate-pulse" />
                          </div>
                          <div className="text-foreground text-sm font-medium">
                            Searching for trending topics...
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                case 'output-available': {
                  const trends = part.output?.trends || []
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="space-y-2">
                        {trends.length > 0 ? (
                          trends.map((trend, index) => (
                            <div
                              key={index}
                              className="border-border bg-card rounded-xl border p-3.5">
                              {/* Header: icon + topic + hook + info tooltip */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2.5">
                                  <div className="bg-primary/10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md">
                                    <TrendingUp className="text-primary h-3.5 w-3.5" />
                                  </div>
                                  <div>
                                    <div className="text-foreground text-sm font-semibold leading-snug">
                                      {trend.topic}
                                    </div>
                                    <div className="text-primary/80 mt-0.5 text-xs italic">
                                      {trend.hook}
                                    </div>
                                  </div>
                                </div>
                                {trend.relevance && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button className="text-muted-foreground hover:text-foreground mt-0.5 shrink-0 transition-colors">
                                        <Info className="h-3.5 w-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs text-xs">
                                      {trend.relevance}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>

                              {/* Content ideas */}
                              {trend.contentIdeas && trend.contentIdeas.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                  {trend.contentIdeas.map((idea, idx) => (
                                    <button
                                      key={idx}
                                      onClick={() => onContentIdeaSelect?.(trend.topic, idea)}
                                      className="border-border hover:border-primary hover:bg-primary/5 text-foreground inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left text-xs transition-all">
                                      <Sparkles className="text-primary h-3 w-3 shrink-0" />
                                      <span>{idea}</span>
                                    </button>
                                  ))}
                                  <button
                                    onClick={() => onBrainstormMore?.(trend.topic)}
                                    className="text-muted-foreground hover:text-primary hover:border-primary inline-flex items-center gap-1.5 rounded-lg border border-dashed border-transparent px-2.5 py-1.5 text-xs transition-all">
                                    <RefreshCw className="h-3 w-3 shrink-0" />
                                    <span>More ideas</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="border-border bg-card rounded-xl border p-4">
                            <div className="text-muted-foreground text-sm">
                              No trending topics found. Try a different industry or broader search.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                }
                case 'output-error':
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-destructive/50 bg-destructive/10 rounded-xl border p-3">
                        <div className="text-destructive text-sm">
                          Couldn't fetch trending topics. Let's try a different approach - what
                          interests you?
                        </div>
                      </div>
                    </div>
                  )
              }
            }

            if (part.type === 'tool-finalizeTopic') {
              const topic = part.input?.topic ?? ''
              const refinedPrompt = part.input?.refinedPrompt ?? ''

              switch (part.state) {
                case 'input-streaming':
                case 'input-available':
                case 'output-available':
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-primary bg-primary/5 rounded-xl border p-4">
                        <div className="flex items-center gap-2">
                          {part.state === 'output-available' ? (
                            <CircleCheck className="h-5 w-5 text-green-500" />
                          ) : (
                            <Lightbulb className="text-primary h-5 w-5" />
                          )}
                          <div className="text-foreground font-medium">
                            {part.state === 'output-available'
                              ? 'Topic finalized!'
                              : 'Ready to create your post!'}
                          </div>
                        </div>
                        <div className="text-foreground mt-3 text-sm">
                          <span className="font-medium">Topic:</span> {topic}
                        </div>
                        {refinedPrompt && refinedPrompt !== topic && (
                          <div className="text-muted-foreground mt-2 text-sm">
                            <span className="font-medium">Refined prompt:</span> {refinedPrompt}
                          </div>
                        )}
                        {onTopicSelect && topic && (
                          <Button
                            size="default"
                            className="mt-4 w-full"
                            onClick={() => onTopicSelect(topic, refinedPrompt)}>
                            <Lightbulb className="mr-2 h-4 w-4" />
                            Generate Post
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                case 'output-error':
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-destructive/50 bg-destructive/10 rounded-xl border p-3">
                        <div className="text-destructive text-sm">
                          Something went wrong. Please try again.
                        </div>
                      </div>
                    </div>
                  )
              }
            }

            return null
          })}
        </TooltipProvider>
      </div>
    </div>
  )
}
